import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";
import { CoreLogService } from "../../services/log.service";
import { Router } from "@angular/router";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { CoreLanguageService } from "../../services/language.service";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { IOSHelperService } from "../../services/IOSHelper.service";
import Utils from "../../shared/utils";
import { Observable, Subscription } from "rxjs";
import { WebShopMode } from "../../shared/contentTypes";
import { IDayList, ISelectedDay, ISelectedTime, IServiceOpenTime } from "./orderTimeSelector.facade";
import { OrderTimeSelectorService } from "./orderTimeSelector.service";

@Component({
  selector: "orderTimeSelector",
  templateUrl: "./orderTimeSelector.component.html",
  styleUrls: ["./orderTimeSelector.component.scss"],
  providers: [OrderTimeSelectorService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class OrderTimeSelectorComponent implements OnInit, OnDestroy {
  private _langSubscriber: Subscription;
  private _viewTypeSubscription: Subscription;

  public serviceId: number;
  public dayList$: Observable<IDayList[]>
  public serviceOpenTime$: Observable<IServiceOpenTime[]>

  //TODO customer settings (portal)
  public get useOrderSubmissionTime(): boolean {
    return this._orderTimeSelectorService.useOrderSubmissionTime;
  }

  /** Service day (Pick up /delivery)*/
  public get selectedDay(): ISelectedDay {
    const me = this;
    return me._orderTimeSelectorService.selectedDay;
  }

  /** Service day (Pick up /delivery)*/
  public get selectedTime(): ISelectedTime {
    const me = this;
    return me._orderTimeSelectorService.selectedTime;
  }

  /** Get as soon as possible value */
  public get orderASAP(): boolean {
    const me = this;
    return me._orderTimeSelectorService.orderASAP;
  }

  /** Check if time selection is available in the order type settings */
  public get hasTimeSelection(): boolean {
    return this._orderTimeSelectorService.hasTimeSelection;
  }

  public isSmallView: boolean = false;

  constructor(private _orderTimeSelectorService: OrderTimeSelectorService
    , private _router: Router
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreLanguageService: CoreLanguageService
    , private _mainnavService: MainnavService
    , private _internationalisationService: InternationalisationService
    , private _iosHelperService: IOSHelperService) { }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe()
    }
    if (me._viewTypeSubscription) {
      me._viewTypeSubscription.unsubscribe();
    }
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("OrderTimeSelectorComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me._orderTimeSelectorService.saveWebshopBaseDataToStorage();
    me._orderTimeSelectorService.initial().then(() => { me.PushChanges(); })
    me.dayList$ = me._orderTimeSelectorService.dayList$;
    me.serviceOpenTime$ = me._orderTimeSelectorService.timeList$;
    me.serviceId = me._orderTimeSelectorService.serviceID;
    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });
    me.onViewTypeChanges(me._mainnavService.viewType);
    me._viewTypeSubscription = me._mainnavService.observeViewTypeChanges.subscribe((viewType) => {
      me.onViewTypeChanges(viewType);
      me.PushChanges();
    });
  }

  /** Checks if the current order is for delivery. */
  public get isOrderDelivery(): boolean {
    const me = this;
    return me.serviceId === WebShopMode.Delivery;

  }

  public get selectTimeInfoTitle() {
    const me = this;
    if (me.useOrderSubmissionTime) {
      return me._coreLanguageService.value("phWebshop_SelectOrderSubmissionTime");
    } else {
      return me._coreLanguageService.value(me.isOrderDelivery ? "phWebshop_SelectDeliveryTime" : "phWebshop_SelectPickupTime");
    }
  }

  public get selectTimeSubText() {
    const me = this;
    if (me.useOrderSubmissionTime) {
      return me._coreLanguageService.value("phWebshop_SelectOrderSubmissionTime_subText");
    } else {
      return me._coreLanguageService.value(me.isOrderDelivery ? "phWebshop_SelectDeliveryTime_subText" : "phWebshop_SelectPickupTime_subText");
    }
  }

  /** Returns the service day label based on the service ID. */
  public get dayLabel() {
    const me = this;
    if (me.useOrderSubmissionTime) {
      return me._coreLanguageService.value("phWebshop_ServiceSubmissionDay_Label");
    } else {
      const deliveryDayLabel = "phWebshop_Delivery_Day_Label";
      const PickupDayLabel = "phWebshop_Pickup_Day_Label";
      return me._coreLanguageService.value(me.isOrderDelivery ? deliveryDayLabel : PickupDayLabel);
    }

  }

  /** Returns the service time label based on the service ID. */
  public get timeLabel() {
    const me = this;
    if (me.useOrderSubmissionTime) {
      return me._coreLanguageService.value("phWebshop_ServiceSubmissionTime_Label");
    } else {
      const deliveryTimeLabel = "phWebshop_Delivery_Time_Label";
      const PickupTimeLabel = "phWebshop_Pickup_Time_Label";
      return me._coreLanguageService.value(me.isOrderDelivery ? deliveryTimeLabel : PickupTimeLabel);
    }

  }

  /**
   * Handles selection change event by updating the selected day.
   * @param event - The selection change event.
   * @param dayList - List of days .
   */
  public async selectionChangeDay(event: any, dayList: IDayList[]) {
    const me = this;
    const selectedValue = event.target.value;
    const selectedDay = dayList.find(day => day.value === selectedValue);
    if (selectedDay) {
      const _selectedDay: ISelectedDay = { value: selectedDay.value, useTime: selectedDay.use_time };
      me._orderTimeSelectorService.setDay(_selectedDay);
      await me._orderTimeSelectorService.setAvailableTimeList();
      me.PushChanges();
    }
  }

  /**  Compares the given day with the currently selected day.*/
  public isDayselected(day: string): boolean {
    const me = this;
    const dateString = Utils.GetDateFromMomentString(day);
    const selectedDay = Utils.GetDateFromMomentString(me.selectedDay.value);
    return dateString === selectedDay;
  }

  /**
 * Handles the change in service time selection.
 * @param index - The index of the selected service time.
 * @param timeList - The array of available service time.
 */
  public onTimeChange(index: string, timeList: IServiceOpenTime[]) {
    const me = this;
    const selectedIndex = +index;
    const serviceTime = timeList[selectedIndex];
    if (!serviceTime) return;
    me._orderTimeSelectorService.setOrderAsap(serviceTime.asap);
    me._orderTimeSelectorService.setSelectedTime({ timeValue: serviceTime.value });
    me.PushChanges();
  }

  /** Checks if the given time is selected.*/
  public isTimeSelected(availableTime: IServiceOpenTime): boolean {
    const me = this;
    const selected = availableTime.value === me.selectedTime.timeValue;
    return selected && me.orderASAP === availableTime.asap;
  }

  /**
   * Checks if the button should be active
   * @returns true if both username and group name inputs are valid.
   */
  public setButtonActive(): boolean {
    const me = this;
    const hasServiceDay = me.selectedDay ? !Utils.IsNullOrEmpty(me.selectedDay.value) : false;
    const hasSericeTime = me.selectedTime ? !Utils.IsNullOrEmpty(me.selectedTime.timeValue) : false;
    return hasServiceDay && hasSericeTime;
  }

  public async handleNavigation() {
    const me = this;
    await me._orderTimeSelectorService.cleanupAndSaveTimeRequirements();
    const cachedBenefitData = await me._orderTimeSelectorService.getBenefitData();
    if (cachedBenefitData) {
      await me._orderTimeSelectorService.redeemBenefitPoints();
    } else {
      me.navigateToMenuGroup();
    }
  }

  /** Navigate to the menu group */
  private navigateToMenuGroup() {
    const me = this;
    me._router.navigate(["menugroup"]);
  }

  /** Set the web view after blur */
  public setWebView() {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

  /** navigate back on swipe from left to right */
  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }

  /** navigate to home screen */
  public navigateBack(): void {
    const me = this;
    me._router.navigate(["/"]);
  }

  private onViewTypeChanges(viewType: ViewType) {
    const me = this;
    me.isSmallView = (viewType === ViewType.Small);
  }
}